import {
  Box,
  SimpleGrid,
  Text,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaFileContract, FaTruck, FaWarehouse } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import type { MVRProductType } from '../schemas';

interface MvrProductTypeOption {
  value: MVRProductType;
  title: string;
  description: string;
  icon: IconType;
}

const MVR_PRODUCT_TYPE_OPTIONS: MvrProductTypeOption[] = [
  {
    value: 'irca',
    title: 'IRCA',
    description: 'Individually Rated Commercial Auto',
    icon: FaFileContract,
  },
  {
    value: 'fleet',
    title: 'Fleet',
    description: 'Vehicle Fleet',
    icon: FaTruck,
  },
  {
    value: 'garage',
    title: 'Garage',
    description: 'Garage / Dealers',
    icon: FaWarehouse,
  },
];

interface MvrProductTypeSelectorProps {
  selectedType: MVRProductType | null;
  onSelect: (type: MVRProductType) => void;
}

const MvrProductTypeSelector = ({
  selectedType,
  onSelect,
}: MvrProductTypeSelectorProps) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const hoverBorderColor = useColorModeValue('gray.300', 'whiteAlpha.300');
  const selectedBorderColor = useColorModeValue('brand.500', 'brand.400');
  const selectedBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  return (
    <Box role="radiogroup" aria-label="MVR request product type">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {MVR_PRODUCT_TYPE_OPTIONS.map((option) => {
          const isSelected = selectedType === option.value;
          return (
            <Box
              key={option.value}
              as="button"
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(option.value)}
              textAlign="left"
              p={5}
              borderWidth="2px"
              borderRadius="12px"
              borderColor={isSelected ? selectedBorderColor : borderColor}
              bg={isSelected ? selectedBg : 'transparent'}
              _hover={{
                borderColor: isSelected
                  ? selectedBorderColor
                  : hoverBorderColor,
              }}
              _focus={{ boxShadow: 'outline', outline: 'none' }}
              transition="all 0.15s"
            >
              <Icon
                as={option.icon}
                boxSize={6}
                color={isSelected ? selectedBorderColor : 'gray.400'}
                mb={3}
              />
              <Text fontWeight="700" color={textColor}>
                {option.title}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {option.description}
              </Text>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export default MvrProductTypeSelector;
